import { route } from 'preact-router';
import { useState } from 'preact/hooks';
import { PaymentPauseNotice } from '../../components/booking/PaymentPauseNotice.js';
import { Badge } from '../../components/ui/Badge.js';
import { Card } from '../../components/ui/Card.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { ResultState } from '../../components/ui/ResultState.js';
import { useFetch } from '../../hooks/useFetch.js';
import { usePaymentPause } from '../../hooks/usePaymentPause.js';
import { ApiError, api } from '../../services/api.js';
import { uploadPaymentReceipt } from '../../utils/receiptUpload.js';
import { type PenaltyDetail, STATE_LABEL, STATE_VARIANT, dayWords, rm } from './penalties.js';

/**
 * 393 (#1313, US2, mock-up screens 2–3): one penalty. Unpaid or rejected: CARii's QR (a 10-minute link, re-read with the
 * page) and the receipt upload: the file goes up as `penalty_receipt` (file before purpose), then is attached. Under
 * review: the receipt, no second upload. Paid: the day.
 */
const uploadedWords = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    timeZone: 'Asia/Kuala_Lumpur',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });

export function PenaltyPage({ id }: { id?: string }) {
  const [version, setVersion] = useState(0);
  const read = useFetch<PenaltyDetail>(id ? `/penalties/mine/${id}` : null, version);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 406 (#1369): the server's own clock decides; a page open across 00:00 swaps the QR without a reload.
  const { paused: pausedNow } = usePaymentPause();

  async function onFile(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file || !id) return;
    setBusy(true);
    setError(null);
    try {
      const fileId = await uploadPaymentReceipt(file);
      await api.post(`/penalties/mine/${id}/receipt`, { file_id: fileId });
      setVersion((v) => v + 1);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Your receipt could not be uploaded. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (read.status !== 'ok' || !read.data) {
    return (
      <div className="bg-bg px-4 py-6">
        <PageHeader title="Penalty" fallback="/account" />
        <ResultState
          state={read}
          onRetry={read.retry}
          goneMessage="This penalty could not be found."
          onwardLabel="Back to my account"
          onOnward={() => route('/account')}
        />
      </div>
    );
  }
  const p = read.data;
  const payable = p.state === 'unpaid' || p.state === 'rejected';
  const paymentsPaused = pausedNow || Boolean(p.payments_paused_until);

  return (
    <div className="bg-bg px-4 py-6">
      <PageHeader title="Penalty" fallback="/account" />
      <Card className="mb-3">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-text">{p.type_label}</span>
          <Badge variant={STATE_VARIANT[p.state]}>{STATE_LABEL[p.state]}</Badge>
        </div>
        <p className="mt-2 text-3xl font-bold text-text">{rm(p.amount_minor)}</p>
        <p className="text-xs text-gray-400">
          {dayWords(p.on)} · {p.car_label} <span className="text-primary">{p.plate_number}</span> ·
          booking #{p.booking_ref}
        </p>
        <p className="mt-3 text-xs text-gray-400">Reason</p>
        <p className="text-sm text-text">{p.reason}</p>
        {p.state === 'rejected' && p.reject_reason && (
          <p className="mt-3 rounded-lg bg-red-900/40 px-3 py-2 text-sm text-red-100">
            <strong>Why it was rejected</strong>
            <br />
            {p.reject_reason}
          </p>
        )}
        {p.state === 'paid' && p.customer_paid_on && (
          <p className="mt-3 text-sm text-green-300">Paid on {dayWords(p.customer_paid_on)}</p>
        )}
      </Card>

      {p.state === 'under_review' && (
        <Card className="mb-3">
          {p.receipt ? (
            <a
              href={p.receipt.url}
              target="_blank"
              rel="noreferrer"
              data-testid="penalty-receipt-link"
              className="text-sm text-primary underline"
            >
              View my receipt
              {p.receipt.uploaded_at ? ` (uploaded ${uploadedWords(p.receipt.uploaded_at)})` : ''}
            </a>
          ) : null}
          <p className="mt-2 text-sm text-gray-400">
            We’ll let you know once staff have checked it.
          </p>
        </Card>
      )}

      {payable && (
        <>
          <Card className="mb-3">
            <div className="text-center">
              <p className="font-semibold text-text">Pay {rm(p.amount_minor)} to CARii</p>
              {paymentsPaused ? (
                // 406 (#1369): no new payment during the banks' Friday downtime; the upload below stays, since it
                // reports a payment already made.
                <PaymentPauseNotice className="mt-2" />
              ) : p.payment_qr_url ? (
                <>
                  <img
                    data-testid="penalty-qr"
                    src={p.payment_qr_url}
                    alt="CARii payment QR"
                    className="mx-auto my-3 h-48 w-48 rounded-lg bg-white object-contain"
                  />
                  <p className="text-xs text-gray-400">
                    Scan with your banking app. Then upload your receipt below.
                  </p>
                </>
              ) : (
                <p className="mt-2 text-sm text-gray-400">
                  Payment details are not available yet. Please contact support.
                </p>
              )}
            </div>
          </Card>
          <label
            data-testid="penalty-upload"
            className={`block w-full cursor-pointer rounded-full bg-primary py-3 text-center font-bold text-black ${busy ? 'opacity-60' : ''}`}
          >
            {busy
              ? 'Uploading…'
              : p.state === 'rejected'
                ? 'Upload a new receipt'
                : 'Upload receipt'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              className="hidden"
              disabled={busy}
              onChange={onFile}
            />
          </label>
          <p className="mt-2 text-center text-xs text-gray-400">
            Photo or PDF. Staff check it, usually within a day.
          </p>
          {error && (
            <p role="alert" className="mt-2 text-center text-sm text-error">
              {error}
            </p>
          )}
        </>
      )}
    </div>
  );
}
